import { Layout, Text } from '@ui-kitten/components';
import React, { useState, useCallback } from 'react';
import { StyleSheet, View, ViewProps } from 'react-native';

import Accordion from 'react-native-collapsible/Accordion';
import HealthRecordHeader from './HealthRecordHeader';
import { ErrorModal } from '../shared/ErrorModal';
import { HealthRecordGroup } from 'src/core/types/ConsolidateHealth';
import HealthRecordList from './HealthRecordList';
import Spinner from 'react-native-loading-spinner-overlay';
import moment from 'moment';
import { UIHelper as uh } from '../../core';
import ConsolidateHealthService from 'src/api/consolidateHealth';
import { useAppStore } from 'src/core/store';

interface IYourHealthRecordsProps extends ViewProps {
  btnDetailHealthRecordClick: (type: string, item: any) => void;
  refreshing: boolean;
  finishRefreshing: () => void;
}

const YourHealthRecords = ({ btnDetailHealthRecordClick, refreshing, finishRefreshing }: IYourHealthRecordsProps) => {
  const styleContainer = StyleSheet.create({
    container: {
      flex: 1
    },
    header: {
      padding: uh.h2DP(16),
      marginTop: uh.h2DP(8),
      borderRadius: 8
    },
    activeHeader: {
      padding: uh.h2DP(16),
      marginTop: uh.h2DP(8),
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8
    },
    content: {
      paddingBottom: uh.h2DP(16),
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8
    },
    healthRecordList: {
      marginTop: uh.h2DP(24),
      paddingLeft: uh.w2DP(16),
      paddingRight: uh.w2DP(16)
    },
    divider: { marginTop: uh.h2DP(39), marginBottom: uh.h2DP(39) },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: uh.h2DP(40)
    },
    emptyText: {
      textAlign: 'center',
      marginTop: uh.h2DP(16)
    }
  });

  const [activeSections, setActiveSections] = useState<number[]>([]);
  const [healthRecords, setHealthRecords] = useState<HealthRecordGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(undefined);
  const patientMapping = useAppStore((state) => state.patientMapping);

  const loadHealthRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);

      const reports = await ConsolidateHealthService.getAllReports();

      const healthRecordGroups: HealthRecordGroup[] = [
        {
          type: 'medications',
          displayName: 'Medications',
          items: reports.medications,
          count: reports.medications.length,
          activeCount: reports.medications.filter((m) => m.status === 'active').length,
          lastUpdated:
            reports.medications.length > 0
              ? new Date(Math.max(...reports.medications.map((c) => new Date(c.updatedOn).getTime())))
              : new Date()
        } as HealthRecordGroup,
        {
          type: 'allergies',
          displayName: 'Allergies',
          items: reports.allergies,
          count: reports.allergies.length,
          activeCount: reports.allergies.filter((a) => a.status === 'active').length,
          lastUpdated:
            reports.allergies.length > 0
              ? new Date(Math.max(...reports.allergies.map((c) => new Date(c.updatedOn).getTime())))
              : new Date()
        } as HealthRecordGroup,
        {
          type: 'conditions',
          displayName: 'Conditions',
          items: reports.conditions,
          count: reports.conditions.length,
          activeCount: reports.conditions.filter((c) => c.status === 'active').length,
          lastUpdated:
            reports.conditions.length > 0
              ? new Date(Math.max(...reports.conditions.map((c) => new Date(c.updatedOn).getTime())))
              : new Date()
        } as HealthRecordGroup,
        {
          type: 'procedures',
          displayName: 'Procedures',
          items: reports.procedures,
          count: reports.procedures.length,
          activeCount: reports.procedures.filter((p) => p.status === 'completed' || p.status === 'active').length,
          lastUpdated:
            reports.procedures.length > 0
              ? new Date(Math.max(...reports.procedures.map((c) => new Date(c.updatedOn).getTime())))
              : new Date()
        } as HealthRecordGroup
      ].filter((group) => group.count > 0); // Only show groups with items

      setHealthRecords(healthRecordGroups);
    } catch (err) {
      console.error('Error loading health records:', err);
      setError(err);
    } finally {
      setLoading(false);
      finishRefreshing();
    }
  }, [finishRefreshing]);

  React.useEffect(() => {
    loadHealthRecords();
  }, [refreshing, loadHealthRecords]);

  const renderHeader = (healthRecordGroup: HealthRecordGroup, index: number, isActive: boolean) => {
    const latestRecordDate = moment(healthRecordGroup.lastUpdated).format('DD MMM YYYY HH:mm');

    return (
      <HealthRecordHeader
        key={`header-index-${index}`}
        healthRecordType={healthRecordGroup.displayName}
        latestRecordDate={latestRecordDate}
        numberOfRecords={healthRecordGroup.count}
        activeRecords={healthRecordGroup.activeCount}
        isActive={isActive}
        onPress={() => {
          // Toggle section
          if (isActive) {
            setActiveSections(activeSections.filter((i) => i !== index));
          } else {
            setActiveSections([...activeSections, index]);
          }
        }}
      />
    );
  };

  const renderContent = (healthRecordGroup: HealthRecordGroup, index: number) => {
    return (
      <View key={`content-index-${index}`} style={styleContainer.content}>
        <HealthRecordList
          healthRecords={healthRecordGroup.items}
          type={healthRecordGroup.type}
          btnDetailHealthRecordClick={(type: string, item: any) => btnDetailHealthRecordClick(type, item)}
        />
      </View>
    );
  };

  if (loading && !refreshing) {
    return <Spinner visible={true} />;
  }

  if (healthRecords.length === 0 && !loading) {
    return (
      <View style={styleContainer.emptyContainer}>
        <Text category="h6">No Health Records</Text>
        {patientMapping == undefined ||
          (patientMapping?.userId == undefined && (
            <Text category="s1" appearance="hint" style={styleContainer.emptyText}>
              Connect your health records to see your medications, allergies, conditions, and procedures here.
            </Text>
          ))}
      </View>
    );
  }

  return (
    <>
      <Layout style={styleContainer.container}>
        <Accordion
          activeSections={activeSections}
          sections={healthRecords}
          renderHeader={renderHeader}
          renderContent={renderContent}
          onChange={setActiveSections}
          underlayColor="transparent"
        />
      </Layout>
      <ErrorModal visible={error != undefined} message={error} />
    </>
  );
};

export default YourHealthRecords;
